import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Lock,
  Truck,
  User,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMyDeliveryProfile,
  useRegisterDeliveryMutation,
} from "@/hooks/queries/useDelivery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  INDIA_STATE_NAMES,
  OTHER_LOCATION,
  getCitiesForState,
} from "@/data/india-locations";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import { cn } from "@/lib/utils";

interface FormState {
  fullName: string;
  password: string;
  confirmPassword: string;
  phone: string;
  email: string;
  vehicleType: string;
  vehicleNumber: string;
  state: string;
  city: string;
  customState: string;
  customCity: string;
  servicePinCodes: string;
  serviceRadius: number;
}

const OTHER_OPTION = [{ value: OTHER_LOCATION, label: "Other (enter manually)" }];

const VEHICLE_TYPES = ["Bike", "Scooter", "Bicycle", "Car", "E-Rickshaw"];

const RegisterDelivery = () => {
  const { isAuthenticated, user, signup, login, refreshUser } = useAuth();
  const { toast } = useToast();
  const registerMutation = useRegisterDeliveryMutation();
  const { data: profile, isLoading: profileLoading, refetch } = useMyDeliveryProfile(isAuthenticated);

  const isNewAccount = !isAuthenticated;
  const totalSteps = isNewAccount ? 2 : 1;

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormState>({
    fullName: "",
    password: "",
    confirmPassword: "",
    phone: "",
    email: "",
    vehicleType: "Bike",
    vehicleNumber: "",
    state: "",
    city: "",
    customState: "",
    customCity: "",
    servicePinCodes: "",
    serviceRadius: 10,
  });

  const isOtherState = formData.state === OTHER_LOCATION;
  const isOtherCity = formData.city === OTHER_LOCATION;
  const cityOptions = isOtherState ? [] : getCitiesForState(formData.state);

  const resolveLocation = () => {
    const stateName = isOtherState ? formData.customState.trim() : formData.state.trim();
    const cityName = isOtherCity || isOtherState ? formData.customCity.trim() : formData.city.trim();
    if (!stateName || !cityName) return "";
    return `${cityName}, ${stateName}`;
  };

  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      fullName: prev.fullName || user.name || "",
      phone: prev.phone || user.phone || "",
      email: prev.email || user.email || "",
    }));
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    let next: string | number = value;

    if (id === "phone") {
      next = value.replace(/\D/g, "").slice(0, 10);
    } else if (id === "servicePinCodes") {
      // Digits and commas only; each pin capped at 6 digits
      next = value
        .replace(/[^\d,\s]/g, "")
        .replace(/\s+/g, "")
        .split(",")
        .map((pin) => pin.slice(0, 6))
        .join(",")
        .slice(0, 80);
    } else if (id === "vehicleNumber") {
      next = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 15);
    } else if (id === "serviceRadius") {
      const n = Number(value);
      next = Number.isFinite(n) ? Math.min(50, Math.max(0, Math.floor(n))) : 0;
    }

    setFormData((prev) => ({
      ...prev,
      [id]: next,
    }));
  };

  const validateStep = () => {
    if (isNewAccount && step === 1) {
      if (!formData.fullName.trim()) {
        toast({ title: "Full name is required", variant: "destructive" });
        return false;
      }
      if (formData.phone.replace(/\D/g, "").length !== 10) {
        toast({ title: "Enter a valid 10-digit mobile number", variant: "destructive" });
        return false;
      }
      if (!formData.email.trim() || !formData.email.includes("@")) {
        toast({ title: "Enter a valid email", variant: "destructive" });
        return false;
      }
      if ((formData.password || "").length < 6) {
        toast({ title: "Password must be at least 6 characters", variant: "destructive" });
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast({ title: "Passwords do not match", variant: "destructive" });
        return false;
      }
      return true;
    }

    if (!formData.fullName.trim()) {
      toast({ title: "Full name is required", variant: "destructive" });
      return false;
    }
    if (formData.phone.replace(/\D/g, "").length !== 10) {
      toast({ title: "Enter a valid 10-digit mobile number", variant: "destructive" });
      return false;
    }
    const pins = formData.servicePinCodes
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (pins.some((p) => !/^\d{6}$/.test(p))) {
      toast({ title: "Each pincode must be exactly 6 digits", variant: "destructive" });
      return false;
    }
    if (!formData.state) {
      toast({ title: "Select a state", variant: "destructive" });
      return false;
    }
    if (isOtherState && !formData.customState.trim()) {
      toast({ title: "Enter your state", variant: "destructive" });
      return false;
    }
    if (isOtherState || isOtherCity) {
      if (!formData.customCity.trim()) {
        toast({ title: "Enter your city", variant: "destructive" });
        return false;
      }
    } else if (!formData.city.trim()) {
      toast({ title: "Select a city", variant: "destructive" });
      return false;
    }
    if (!formData.vehicleType) {
      toast({ title: "Select a vehicle type", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Account step: Continue / Enter must only advance — never call register APIs
    if (isNewAccount && step === 1) {
      if (validateStep()) setStep(2);
      return;
    }

    if (!validateStep()) return;

    const city = resolveLocation();
    if (!city) {
      toast({ title: "Select state and city", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isNewAccount) {
        const registeredUser = await signup({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          password: formData.password,
        });
        if (!registeredUser) {
          throw new Error("Could not create account. Check details and try again.");
        }
        const loggedIn = await login(registeredUser.username, formData.password);
        if (!loggedIn) {
          throw new Error("Account created but login failed. Please sign in and continue.");
        }
      }

      await registerMutation.mutateAsync({
        fullName: formData.fullName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined,
        vehicleType: formData.vehicleType,
        vehicleNumber: formData.vehicleNumber.trim() || undefined,
        city,
        servicePinCodes: formData.servicePinCodes.trim() || undefined,
        serviceRadius: formData.serviceRadius || 10,
      });

      toast({
        title: "Application submitted",
        description: "Your driver registration is pending admin approval.",
      });
      await refreshUser();
      try {
        await refetch();
      } catch {
        /* ignore */
      }
    } catch (err) {
      toast({
        title: "Registration failed",
        description: getApiErrorMessage(err, "Could not submit driver application."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthenticated && profileLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 py-10">
        <Clock className="h-9 w-9 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Checking your driver profile…</p>
      </div>
    );
  }

  if (profile) {
    const pending = profile.status === "pending";
    const active = profile.status === "active";
    const suspended = profile.status === "suspended" || profile.status === "rejected";

    return (
      <Card className="border-border/80 shadow-sm">
        <CardHeader className="space-y-3 text-center">
          <div
            className={cn(
              "mx-auto flex h-14 w-14 items-center justify-center rounded-full",
              active && "bg-emerald-500/15 text-emerald-600",
              pending && "bg-amber-500/15 text-amber-600",
              suspended && "bg-destructive/15 text-destructive",
              !active && !pending && !suspended && "bg-muted text-muted-foreground",
            )}
          >
            {active ? <CheckCircle2 className="h-7 w-7" /> : pending ? <Clock className="h-7 w-7" /> : <Ban className="h-7 w-7" />}
          </div>
          <CardTitle className="text-xl">
            {active ? "You're approved" : pending ? "Application under review" : "Application not active"}
          </CardTitle>
          <CardDescription>
            {active
              ? "You can open the delivery partner dashboard and go online."
              : pending
                ? "Admin will verify your details soon. You'll get access once approved."
                : "Contact support if you need help with your driver account."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 rounded-xl border bg-muted/40 px-4 py-3 text-sm">
          <p>
            <span className="text-muted-foreground">Name:</span> {profile.name}
          </p>
          <p>
            <span className="text-muted-foreground">Phone:</span> {profile.phone}
          </p>
          <p>
            <span className="text-muted-foreground">Vehicle:</span> {profile.vehicleType}
            {profile.vehicleNumber ? ` · ${profile.vehicleNumber}` : ""}
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 sm:flex-row">
          {active ? (
            <Button className="w-full" asChild>
              <Link to="/delivery">Open delivery dashboard</Link>
            </Button>
          ) : (
            <Button className="w-full" asChild>
              <Link to="/">Back to home</Link>
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  }

  const onAccountStep = isNewAccount && step === 1;

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Truck className="h-4 w-4 text-primary" />
          Step {step} of {totalSteps}
          <span className="text-foreground">
            · {onAccountStep ? "Create account" : "Driver details"}
          </span>
        </div>
        {isAuthenticated && user ? (
          <p className="truncate text-xs text-muted-foreground">Signed in as {user.username || user.email}</p>
        ) : null}
      </div>

      {onAccountStep ? (
        <div className="space-y-4 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="pl-9"
                placeholder="As on your ID"
                autoComplete="name"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit mobile"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                maxLength={100}
                autoComplete="email"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-9 pr-10"
                  placeholder="Min 6 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password *</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full name *</Label>
              <Input id="fullName" value={formData.fullName} onChange={handleChange} placeholder="Driver name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10-digit mobile"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Optional"
              maxLength={100}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Vehicle type *</Label>
              <Select
                value={formData.vehicleType}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, vehicleType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicleNumber">Vehicle number</Label>
              <Input
                id="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                placeholder="e.g. BR01AB1234"
                className="uppercase"
                maxLength={15}
                autoCapitalize="characters"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>State *</Label>
              <SearchableSelect
                value={formData.state}
                onValueChange={(state) =>
                  setFormData((prev) => ({
                    ...prev,
                    state,
                    city: "",
                    customCity: state === OTHER_LOCATION ? prev.customCity : "",
                    customState: state === OTHER_LOCATION ? prev.customState : "",
                  }))
                }
                options={INDIA_STATE_NAMES}
                placeholder="Search state…"
                searchPlaceholder="Type to search state…"
                footerOptions={OTHER_OPTION}
              />
              {isOtherState && (
                <Input
                  id="customState"
                  value={formData.customState}
                  onChange={handleChange}
                  placeholder="Enter state / UT name"
                  className="mt-2"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              {isOtherState ? (
                <Input
                  id="customCity"
                  value={formData.customCity}
                  onChange={handleChange}
                  placeholder="Enter city name"
                  disabled={!formData.state}
                />
              ) : (
                <>
                  <SearchableSelect
                    value={formData.city}
                    onValueChange={(city) =>
                      setFormData((prev) => ({
                        ...prev,
                        city,
                        customCity: city === OTHER_LOCATION ? prev.customCity : "",
                      }))
                    }
                    options={cityOptions}
                    placeholder={formData.state ? "Search city…" : "Select state first"}
                    searchPlaceholder="Type to search city…"
                    disabled={!formData.state}
                    footerOptions={OTHER_OPTION}
                  />
                  {isOtherCity && (
                    <Input
                      id="customCity"
                      value={formData.customCity}
                      onChange={handleChange}
                      placeholder="Enter city name"
                      className="mt-2"
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="serviceRadius">Service radius (km)</Label>
            <Input
              id="serviceRadius"
              type="number"
              min={1}
              max={50}
              inputMode="numeric"
              value={formData.serviceRadius}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="servicePinCodes">Service pincodes</Label>
            <Input
              id="servicePinCodes"
              value={formData.servicePinCodes}
              onChange={handleChange}
              placeholder="6-digit pins, e.g. 800001,800002"
              inputMode="numeric"
              maxLength={80}
            />
            <p className="text-xs text-muted-foreground">Each pincode must be 6 digits. Separate multiple with commas.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        {step > 1 ? (
          <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
          </Button>
        ) : (
          <Button type="button" variant="ghost" asChild>
            <Link to="/auth/login">Already have an account? Sign in</Link>
          </Button>
        )}

        {onAccountStep ? (
          <Button type="submit">
            Continue <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        ) : (
          <Button type="submit" disabled={isSubmitting || registerMutation.isPending}>
            {isSubmitting || registerMutation.isPending ? "Submitting…" : "Submit application"}
          </Button>
        )}
      </div>
    </form>
  );
};

export default RegisterDelivery;
